import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { secureDelete, securePost, secureGet, securePut } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGetPdf } from "../../config/api";
import DeletePopup from "../../components/DeletePopup";
import GeneratePopup from "../../components/GeneratePopUp";
import ProductRow from "../../components/ProductRow";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Breadcrumbs from "../../components/Breadcrumbs";
import UserContext from "../../Shared/UserContext";

import trashIcon from "../../icons/trashW.svg";
import editIcon from "../../icons/editB.svg";
import checkIcon from "../../icons/check.svg";
import rejectIcon from "../../icons/reject.svg";
import EyeIcon from "../../icons/eyeB.svg";
import downloadIcon from "../../icons/export.svg";

const STATUS_COLORS = {
  CREATED: "#0283F4",
  UNDER_APPROVAL: "#FEA26F",
  VALIDATED: "#97DFFC",
  DELIVERED: "#06D6A0",
  DECLINED: "#EF466F",
  CANCELLED: "#A8ADC3",
};

const getStateStyle = (status) => ({
  backgroundColor: STATUS_COLORS[status] || "#38485C",
});

const OrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { state: { role } } = useContext(UserContext);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/carts/${id}`, showSnackbar, { signal });
      const fetchedOrder = response.data;
      setOrder(fetchedOrder);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchOrder();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchOrder]);

  const getStatusLabel = (status) => t(`order_status.${status}`) || "N/A";

  const getStatut = (order) => ({
    DV: order?.quotes?.length > 0,
    BC: true,
    BL: order?.deliveryNote !== null,
    FA: !(order.deliveryNote === null || order.deliveryNote?.invoice === null),
  });

  const renderStatut = (statut) => (
    <div className="flex items-center ">
      {["DV", "BC", "BL", "FA"].map((key, idx, arr) => (
        <React.Fragment key={key}>
          <span
            className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${statut[key] ? "bg-[#0AD69F] text-white cursor-pointer" : "bg-[#EF476F] text-white cursor-not-allowed"
              }`}
            onClick={() => statut[key] && handleStatutClick(key)}
          >
            {key}
          </span>
          {idx < arr.length - 1 && (
            <div className="w-2 h-[2px] bg-[#043F88]"></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const handleStatutClick = (key) => {
    switch (key) {
      case "DV":
        handleConsultDV();
        break;
      case "BL":
        handleConsultBL();
        break;
      case "FA":
        handleConsultFA();
        break;
      default:
    }
  };

  const handleConsultDV = () => {
    history.push({
      pathname: `/quotes/details/${order.quotes?.id}`,
    });
  };

  const handleConsultBL = () => {
    history.push({
      pathname: `/delivery-notes/details/${order.deliveryNote.id}`,
    });
  };

  const handleConsultFA = () => {
    history.push({
      pathname: `/invoices/details/${order.deliveryNote.invoice.id}`,
    });
  };


  const handleModifyClick = () => {
    history.push({
      pathname: `/orders/details/modify/${order.id}`,
      state: { filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
    });
  };

  const handleDeleteClick = () => {
    setDeletePopupVisible(true);
  };
  const handleDeleteConfirm = async () => {
    try {
      await secureDelete(`/carts/delete/${order.id}`, showSnackbar);
      setDeletePopupVisible(false);
      history.push({
        pathname: "/orders",
        state: {
          showSnackbar: true,
          snackbarMessage: t(`confirmation.orderDelete`),
          snackbarSeverity: 'success',
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });
    } catch (error) {
      console.error("Error deleting the order:", error);
      setDeletePopupVisible(false);
    }
  };

  const handleValidateClick = async () => {
    try {
      const payload = {
        cartId: order.id,
        status: "VALIDATED",
      };

      await securePost(`/carts/updateStatus`, payload, showSnackbar);
      showSnackbar(t(`confirmation.orderValidation`), 'success');
      fetchOrder();
    } catch (error) {
      console.error("Error validating the order:", error);
    }
  };

  const handleRejectClick = async () => {
    try {
      const payload = {
        cartId: order.id,
        status: "DECLINED",
      };
      await securePost(`/carts/updateStatus`, payload, showSnackbar);
      showSnackbar(t(`confirmation.orderRejection`), 'success');
      fetchOrder();
    } catch (error) {
      console.error("Error rejecting the order:", error);
    }
  };

  const handleMarkDelivered = async () => {
    try {
      const payload = {
        cartId: order.id,
        status: "DELIVERED",
      };
      await securePost(`/carts/updateStatus`, payload, showSnackbar);
      showSnackbar(t(`confirmation.orderDelivery`), 'success');
      fetchOrder();
    } catch (error) {
      console.error("Error marking the order as delivered:", error);
    }
  };

  const handleGenerateBLClick = () => {
    setPopupOpen(true);
  };

  const handleConfirm = async () => {

    try {
      await securePut("deliveryNotes/add", { cartId: order.id }, showSnackbar);
      showSnackbar(t(`confirmation.orderBlGenerated`), 'success');
      fetchOrder();
    } catch (error) {
      console.error("Failed to generate BL:", error);
    } finally {
      setPopupOpen(false);
    }
  };

  const handleCancel = () => {
    setPopupOpen(false);
  };

  const handleDownloadClick = async () => {
    try {
      const response = await secureGetPdf(`/carts/${order.id}/pdf`, showSnackbar);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commande/${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download BL:", error);
    }
  };

  const renderActionButtons = (state) => {
    const orderStatut = getStatut(order);
    if (state === "CREATED") {
      return (
        <>
          <button
            className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-4 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
            onClick={handleValidateClick}>
            <img src={checkIcon} alt="" className="w-5 h-5" />

            {t(`actions.validate`)}
          </button>
          <button
            className="flex items-center  justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2 "
            onClick={handleModifyClick}>
            <img src={editIcon} alt="" className="w-5 h-5" />

            {t(`actions.modify`)}
          </button>
          <button
            className="flex items-center justify-center bg-[#EF476F] text-[#FFFFFF] font-bold text-[14px] px-4 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
            onClick={handleDeleteClick}>
            <img src={trashIcon} alt="" className="w-5 h-5" />

            {t(`actions.delete`)}
          </button>
        </>
      );
    } else if (state === "UNDER_APPROVAL") {
      return (
        <>
          {role === "ROLE_ADMIN" && (
            <>
              <button
                className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-6 py-3 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
                onClick={handleValidateClick}
              >
                <img src={checkIcon} alt="" className="w-5 h-5" />
                {t(`actions.validate`)}
              </button>
              <button
                className="flex items-center justify-center bg-[#EF476F] text-[#FFFFFF] font-bold text-[14px] px-6 py-3 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
                onClick={handleRejectClick}
              >
                <img src={rejectIcon} alt="" className="w-5 h-5" />
                {t(`actions.reject`)}
              </button>

            </>
          )}
          <button
            className="flex items-center  justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
            onClick={handleModifyClick}>
            <img src={editIcon} alt="" className="w-5 h-5" />

            {t(`actions.modify`)}
          </button>
        </>
      );
    } else if (state === "VALIDATED") {
      return (
        <>
          <button
            className="flex items-center justify-center bg-[#043F88] text-[#FFFFFF] font-bold text-[14px] px-4 rounded-[12px] shadow-[0px_20px_80px_#00000011] "
            onClick={handleMarkDelivered}>
            {t(`actions.mark_as_delivered`)}
          </button>
          {!orderStatut.BL && (
            <button
              className="flex items-center justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
              onClick={handleGenerateBLClick}
            >
              <img src={EyeIcon} alt="icon" className="w-5 h-5" />
              {t("actions.generateBL")}
            </button>
          )}
          <button
            className="flex items-center  justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
            onClick={handleDownloadClick}>
            <img src={downloadIcon} alt="" className="w-5 h-5" />

            {t(`actions.download_order`)}
          </button>
          {!orderStatut.BL && (
            <button
              className="flex items-center justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
              onClick={handleModifyClick}>
              <img src={editIcon} alt="" className="w-5 h-5" />
              {t(`actions.modify`)}
            </button>
          )}

        </>
      );
    } else if (state === "CANCELLED") {
      return (
        <button
          className="flex items-center  justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
          onClick={handleModifyClick}>
          <img src={editIcon} alt="" className="w-5 h-5" />

          {t(`actions.modify`)}
        </button>
      );
    } else if (state === "DELIVERED") {
      return (
        <>
          {!orderStatut.BL && (
            <button
              className="flex items-center justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
              onClick={handleGenerateBLClick}
            >
              <img src={EyeIcon} alt="icon" className="w-5 h-5" />
              {t("actions.generateBL")}
            </button>
          )}
          <button
            className="flex items-center  justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
            onClick={handleDownloadClick}>
            <img src={downloadIcon} alt="" className="w-5 h-5" />

            {t(`actions.download_order`)}
          </button>
        </>
      );
    }
    return null;
  };


  if (loading) return <LoadingLogo />;

  if (!order) return <p>{t("errors.order_not_found")}</p>;

  const orderStatut = getStatut(order);
  const orderState = {
    label: getStatusLabel(order.status).toUpperCase(),
    style: getStateStyle(order.status),
  };

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.order_details")}</title>
      </Helmet>
      {/* Header Section */}
      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-[#000000] ">{t(`titles.order_details`)}</h1>
        {/* State and Actions */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <span
              className="px-2 py-1 rounded-full text-[12px] font-semibold text-white text-center"
              style={orderState.style}
            >
              {orderState.label}
            </span>
            <p className="text-[#000000] font-semibold text-[24px]">
              {order.cartNumber}
            </p>
            {renderStatut(orderStatut)}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4">{renderActionButtons(order.status)}</div>

        </div>

        {/* Client Info */}
        <p className="text-[#38485C] text-[20px] font-semibold">
          {order.client?.code} - {order.client?.name}
        </p>
      </div>

      {/* Order Information */}
      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        {/* DATE DE CRÉATION */}
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t(`rows.creationDate`)}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(order.createdAt)}
          </p>
        </div>

        {/* COMMERCIAL */}
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t(`rows.commercial`)}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {order.user.firstName} {order.user.lastName}
          </p>
        </div>

        {/* STOCK */}
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">STOCK</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{order.repo?.name}</p>
        </div>

        {/* MOYEN DE PAIEMENT */}
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t(`rows.paymentMode`)}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{order.paymentMode}</p>
        </div>

        {/* TOTAL HT */}
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL HT</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(order.priceHT)}</p>
        </div>

        {/* TOTAL TTC */}
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(order.priceTTC)}</p>
        </div>
      </div>

      {/* Comment Section */}
      <div className=" py-4 pl-[32px] bg-white rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-6">
        <p className="text-[#707070] text-[14px] font-semibold mb-1">{t(`rows.comment`)}</p>
        <p className="text-[#38485C] text-[20px] font-semibold">
          {order.comment || t("placeholder.no_comment")}
        </p>
      </div>

      {/* Product List */}
      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t(`fields_types.list`)}
      </h3>
      <div className="space-y-2">
        {order.details.map((product, index) => (
          <ProductRow key={index} product={product} />
        ))}
      </div>

      {/* Delete Popup */}
      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Generate BL Popup */}
      <GeneratePopup
        open={isPopupOpen}
        entity={t(`entity.bl`)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />

    </div>
  );
};

export default OrderDetails;

